<script setup lang="ts">
import '~/components/mm/tools/prototype-controls.css'
import '~/components/mm/tools/cover/cover.css'
import '~/components/mm/clips/landing.css';
import { buildClipDraftLocation } from '~/utils/clip-workspace.mjs';
import { createGrowthEventId } from '~/utils/growthEvents';
const router = useRouter();
const { t } = useI18n();
const startNewClipWorkspace = () => router.push(buildClipDraftLocation('new', createGrowthEventId()));
definePageMeta({
  layout: false,
});

useHead({
  title: 'MusicMint 短视频素材包',
  meta: [
    {
      name: 'description',
      content: '把短视频需求变成原创音乐、MV 草稿、授权证明和可分享的短视频素材包。',
    },
  ],
});
</script>

<template>
  <main class="mm-root t24-tools cover-workspace wrap kit-page" data-page="short-video-kit">
      <nav class="kit-nav" :aria-label="t('t24c.kitNav')">
        <NuxtLink class="kit-brand" to="/">
          <img class="kit-brand-mark" src="/images/logo.png" alt="" width="32" height="32" />
          <strong>MusicMint</strong>
        </NuxtLink>
        <div class="kit-nav-actions">
          <NuxtLink class="kit-nav-link" to="/clips">{{ t('t24c.kitWorkspace') }}</NuxtLink>
          <button class="btn btn-secondary kit-nav-cta" type="button" @click="startNewClipWorkspace">{{ t('t24c.kitStart') }}</button>
        </div>
      </nav>
    <section class="kit-hero c-layout">





      <div class="kit-hero-content c-form">
        <p class="kit-eyebrow">{{ t('t24c.kitEyebrow') }}</p>
        <h1>{{ t('t24c.kitHeading') }}</h1>
        <p class="kit-hero-copy">
          {{ t('t24c.kitIntro') }}
        </p>
        <div class="kit-hero-actions">
          <button class="btn btn-primary kit-primary" type="button" @click="startNewClipWorkspace">{{ t('t24c.kitStart') }}</button>
          <a class="btn btn-secondary kit-secondary" href="#demo">{{ t('t24c.kitExample') }}</a>
        </div>
        <dl class="kit-metrics">
          <div>
            <dt>20s</dt>
            <dd>TikTok / Reels / Shorts</dd>
          </div>
          <div>
            <dt>{{ t('t24c.kitTwo') }}</dt>
            <dd>{{ t('t24c.kitTracks') }}</dd>
          </div>
          <div>
            <dt>39.5s</dt>
            <dd>{{ t('t24c.kitClip') }}</dd>
          </div>
        </dl>
      </div>
      <div class="stage">      <video
        class="kit-hero-video" width="1080" height="1920"
        controls
        playsinline
        preload="metadata"
        data-demo-video="flexbar-protein"
        poster="/clips/flexbar-protein-poster.jpg"
        src="/clips/flexbar-protein-demo.mp4"
      ></video></div>
    </section>

    <section class="kit-section">
      <div class="kit-section-head">
        <p class="kit-eyebrow">{{ t('t24c.kitAudience') }}</p>
        <h2>{{ t('t24c.kitAudienceHeading') }}</h2>
      </div>
      <div class="kit-audience-grid">
        <article>
          <span class="i-lucide-package" aria-hidden="true"></span>
          <h3>{{ t('t24c.kitBrand') }}</h3>
          <p>{{ t('t24c.kitBrandBody') }}</p>
        </article>
        <article>
          <span class="i-lucide-clapperboard" aria-hidden="true"></span>
          <h3>{{ t('t24c.kitTeam') }}</h3>
          <p>{{ t('t24c.kitTeamBody') }}</p>
        </article>
        <article>
          <span class="i-lucide-megaphone" aria-hidden="true"></span>
          <h3>{{ t('t24c.kitAgency') }}</h3>
          <p>{{ t('t24c.kitAgencyBody') }}</p>
        </article>
      </div>
    </section>

    <section class="kit-section kit-workflow">
      <div class="kit-section-head">
        <p class="kit-eyebrow">{{ t('t24c.kitWorkflow') }}</p>
        <h2>{{ t('t24c.kitWorkflowHeading') }}</h2>
      </div>
      <ol class="kit-steps">
        <li><strong>{{ t('t24c.kitBrief') }}</strong><span>{{ t('t24c.kitBriefBody') }}</span></li>
        <li><strong>{{ t('t24c.kitGenerate') }}</strong><span>{{ t('t24c.kitGenerateBody') }}</span></li>
        <li><strong>{{ t('t24c.kitChoose') }}</strong><span>{{ t('t24c.kitChooseBody') }}</span></li>
        <li><strong>{{ t('t24c.kitDraft') }}</strong><span>{{ t('t24c.kitDraftBody') }}</span></li>
        <li><strong>{{ t('t24c.kitShare') }}</strong><span>{{ t('t24c.kitShareBody') }}</span></li>
      </ol>
    </section>

    <section id="demo" class="kit-section kit-demo">
      <div class="kit-section-head">
        <p class="kit-eyebrow">{{ t('t24c.kitRealExample') }}</p>
        <h2>FlexBar <span class="t-serif">Protein</span> launch ad</h2>
        <p>{{ t('t24c.kitExampleBody') }}</p>
      </div>
      <div class="kit-demo-video-panel">
        <div class="kit-demo-video-copy">
          <p class="kit-demo-badge">{{ t('t24c.kitGeneratedVideo') }}</p>
          <h3>{{ t('t24c.kitVertical') }}</h3>
          <p>
            {{ t('t24c.kitVerticalBody') }}
          </p>
        </div>
        <video
          class="kit-demo-video" width="1080" height="1920"
          controls
          playsinline
          preload="metadata"
          data-demo-placement="case-study"
          data-demo-video="flexbar-protein-case-study"
          poster="/clips/flexbar-protein-poster.jpg"
          src="/clips/flexbar-protein-demo.mp4"
        ></video>
      </div>
      <div class="kit-demo-grid">
        <article class="kit-demo-input">
          <h3>{{ t('t24c.kitInput') }}</h3>
          <p>
            New protein bar launch. Need a punchy 20 second TikTok/Reels ad for pre/post gym moments.
            Upload product image, then generate original music and an MV-ready draft.
          </p>
          <dl>
            <div><dt class="field-label">{{ t('t24c.kitProduct') }}</dt><dd>FlexBar Protein</dd></div>
            <div><dt class="field-label">{{ t('t24c.kitPlatform') }}</dt><dd>TikTok / Reels / Shorts</dd></div>
            <div><dt class="field-label">{{ t('t24c.kitMood') }}</dt><dd>Energetic, confident</dd></div>
            <div><dt class="field-label">{{ t('t24c.kitStyle') }}</dt><dd>phonk, funk</dd></div>
          </dl>
        </article>
        <article class="kit-demo-output">
          <h3>{{ t('t24c.kitOutput') }}</h3>
          <ul>
            <li><span class="i-lucide-check" aria-hidden="true"></span>{{ t('t24c.kitOut1') }}</li>
            <li><span class="i-lucide-check" aria-hidden="true"></span>{{ t('t24c.kitOut2') }}</li>
            <li><span class="i-lucide-check" aria-hidden="true"></span>{{ t('t24c.kitOut3') }}</li>
            <li><span class="i-lucide-check" aria-hidden="true"></span>{{ t('t24c.kitOut4') }}</li>
          </ul>
        </article>
      </div>
    </section>

    <section class="kit-final-cta">
      <p class="kit-eyebrow">{{ t('t24c.kitBegin') }}</p>
      <h2>{{ t('t24c.kitFinal') }}</h2>
      <p>{{ t('t24c.kitFinalBody') }}</p>
      <button class="btn btn-primary kit-primary" type="button" @click="startNewClipWorkspace">{{ t('t24c.kitFinalAction') }}</button>
    </section>
  </main>
</template>
